import { useNavigate } from "react-router-dom";
import { Stethoscope, CheckCircle2, Clock, Ban } from "lucide-react";
import toast from "react-hot-toast";
import PieChart, { PieChartDatum } from "./PieChart";
import MetricsStrip from "./MetricsStrip";
import { STATUS_COLORS } from "./statusColors";
import { DoctorStats } from "../../services/superAdmin.service";
import { getRole } from "../../utils/auth";
import ROLES from "../../utils/constants";
import { OverviewCardSkeleton } from "../common/Skeleton";
import WidgetErrorState from "../common/WidgetErrorState";
import EmptyState from "../common/EmptyState";
import StaleDataBanner from "../common/StaleDataBanner";

export interface DoctorOverviewProps {
  stats: DoctorStats | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  isStale?: boolean;
}

const DoctorOverview = ({ stats, loading, error, onRetry, isStale = false }: DoctorOverviewProps) => {
  const navigate = useNavigate();

  const goToDoctors = (params?: { status?: string }) => {
    const role = getRole();
    if (role !== ROLES.SUPER_ADMIN) {
      toast.error("You do not have permission to view doctors");
      return;
    }
    const search = new URLSearchParams();
    if (params?.status) search.set("status", params.status);
    const qs = search.toString();
    navigate(qs ? `/doctors?${qs}` : "/doctors");
  };

  if (loading && !stats) {
    return <OverviewCardSkeleton />;
  }

  if (error && !stats) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E4EAF0] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E4EAF0] flex items-center justify-between bg-gray-50/30">
          <h2 className="text-lg font-bold text-[#10253A] flex items-center gap-2.5">
            <Stethoscope className="w-5 h-5 text-primary-500" aria-hidden="true" />
            Doctor Overview
          </h2>
        </div>
        <WidgetErrorState
          title="Doctor Statistics Unavailable"
          message={error}
          onRetry={onRetry}
          variant="card"
          className="border-0 shadow-none"
        />
      </div>
    );
  }

  const pieData: PieChartDatum[] = stats
    ? [
        { label: "Active", value: stats.active, color: STATUS_COLORS.active },
        { label: "In Progress", value: stats.inProgress, color: STATUS_COLORS.inProgress },
        { label: "Inactive", value: stats.inactive, color: STATUS_COLORS.inactive },
      ]
    : [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E4EAF0] overflow-hidden">
      {/* Card header */}
      <div className="px-6 py-4 border-b border-[#E4EAF0] flex items-center justify-between bg-gray-50/30">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-[#10253A] flex items-center gap-2.5">
            <Stethoscope className="w-5 h-5 text-primary-500" aria-hidden="true" />
            Doctor Overview
          </h2>
        </div>
        {loading && (
          <span className="text-xs text-[#63788E] flex items-center gap-1.5 animate-pulse">
            <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
            Updating...
          </span>
        )}
      </div>

      {isStale && (
        <div className="p-4 border-b border-[#E4EAF0]">
          <StaleDataBanner
            message="Unable to refresh latest doctor statistics. Showing cached numbers."
            onRetry={onRetry}
          />
        </div>
      )}

      {stats ? (
        <div className="p-6 space-y-6">
          <MetricsStrip
            items={[
              {
                key: "total",
                label: "Doctors",
                value: stats.total,
                icon: <Stethoscope size={14} aria-hidden="true" />,
                color: STATUS_COLORS.active,
                onClick: () => goToDoctors(),
              },
              {
                key: "active",
                label: "Active",
                value: stats.active,
                icon: <CheckCircle2 size={14} aria-hidden="true" />,
                color: STATUS_COLORS.active,
                onClick: () => goToDoctors({ status: "active" }),
              },
              {
                key: "in-progress",
                label: "In Progress",
                value: stats.inProgress,
                icon: <Clock size={14} aria-hidden="true" />,
                color: STATUS_COLORS.inProgressText,
                onClick: () => goToDoctors({ status: "in_progress" }),
              },
              {
                key: "inactive",
                label: "Inactive",
                value: stats.inactive,
                icon: <Ban size={14} aria-hidden="true" />,
                color: STATUS_COLORS.inactive,
                onClick: () => goToDoctors({ status: "inactive" }),
              },
            ]}
          />

          {stats.total === 0 ? (
            <EmptyState
              icon={<Stethoscope size={24} />}
              title="No doctors registered yet"
              description="Add your first doctor to enable MDT reviews, patient assignments, and consultations."
              action={{
                label: "Add Doctor",
                to: "/doctors/new",
              }}
              variant="compact"
            />
          ) : (
            <div className="flex items-center justify-center">
              <PieChart data={pieData} centerLabel="Doctors" emptyLabel="No doctors registered" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default DoctorOverview;
